import apiService, { ApiError } from './apiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

class DashboardService {
  /**
   * Format date for API (YYYY-MM-DD HH:mm:ss)
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const time = isEndDate ? '23:59:59' : '00:00:00';
    return `${y}-${m}-${day} ${time}`;
  }

  /**
   * Fetch comprehensive dashboard data from both skai-one and skai-net in parallel
   * @param {Object} params
   * @param {string|Date} params.startDate
   * @param {string|Date} params.endDate
   * @returns {Promise<Object>} Unified dashboard state
   */
  async getDashboardData({ startDate, endDate } = {}) {
    const payload = {};
    if (startDate && endDate) {
      payload.dateFilter = {
        start: this.formatDateForAPI(startDate, false),
        end: this.formatDateForAPI(endDate, true),
      };
    }

    const [skaiOneResult, skaiNetResult] = await Promise.allSettled([
      apiService.skaiOneRequest(API_ENDPOINTS.DASHBOARD.SUMMARY, {
        method: 'POST',
        data: payload,
      }),
      apiService.skaiNetRequest(API_ENDPOINTS.DASHBOARD.CREATOR_STATS, {
        method: 'POST',
        data: payload,
      }),
    ]);

    let skaiOneData = null;
    if (skaiOneResult.status === 'fulfilled' && skaiOneResult.value?.data) {
      skaiOneData = skaiOneResult.value.data;
    } else if (skaiOneResult.status === 'rejected') {
      console.warn('Dashboard summary skai-one fetch failed:', skaiOneResult.reason);
      throw skaiOneResult.reason instanceof ApiError
        ? skaiOneResult.reason
        : new ApiError('Failed to fetch dashboard summary from skai-one', 500);
    }

    let creatorStats = {
      totalMarketers: 0,
      activeMarketers: 0,
      newMarketersInPeriod: 0,
      priorMarketersInPeriod: 0,
      creatorGrowthRate: 0,
      dailyCreators: {},
    };
    if (skaiNetResult.status === 'fulfilled' && skaiNetResult.value?.data) {
      const netData = skaiNetResult.value.data;
      creatorStats = {
        totalMarketers: netData.totalMarketers || 0,
        activeMarketers: netData.activeMarketers || 0,
        newMarketersInPeriod: netData.newMarketersInPeriod || 0,
        priorMarketersInPeriod: netData.priorMarketersInPeriod || 0,
        creatorGrowthRate: netData.creatorGrowthRate || 0,
        dailyCreators: netData.dailyCreators || {},
      };
    } else if (skaiNetResult.status === 'rejected') {
      console.warn('Dashboard creator stats skai-net fetch failed:', skaiNetResult.reason);
    }

    const metrics = skaiOneData?.metrics || {};
    const totalCommunity = (metrics.totalShoppers || 0) + (creatorStats.totalMarketers || 0);
    const newCommunityInPeriod = (metrics.newShoppersInPeriod || 0) + (creatorStats.newMarketersInPeriod || 0);

    // Merge daily creator counts into volumeTrend points
    const volumeTrend = (skaiOneData?.volumeTrend || []).map(pt => ({
      ...pt,
      newCreators: creatorStats.dailyCreators[pt.date] || 0,
    }));

    return {
      metrics: {
        ...metrics,
        totalCreators: creatorStats.totalMarketers,
        activeCreators: creatorStats.activeMarketers,
        newCreatorsInPeriod: creatorStats.newMarketersInPeriod,
        priorCreatorsInPeriod: creatorStats.priorMarketersInPeriod,
        creatorGrowthRate: creatorStats.creatorGrowthRate,
        totalCommunity,
        newCommunityInPeriod,
      },
      volumeTrend,
      pendingQueue: skaiOneData?.pendingQueue || { snaps: [], reversals: [] },
      topMerchants: skaiOneData?.topMerchants || [],
      recentActivities: skaiOneData?.recentActivities || [],
    };
  }

  /**
   * Currency formatter helper
   */
  formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return '€0.00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Number formatter with compact notation option
   */
  formatNumber(value, compact = false) {
    if (value === null || value === undefined || isNaN(value)) return '0';
    if (compact && value >= 1000) {
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);
    }
    return new Intl.NumberFormat('it-IT').format(value);
  }

  /**
   * Relative time formatter
   */
  formatTimeAgo(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const diffSecs = Math.floor((new Date() - date) / 1000);

    if (diffSecs < 60) return 'Just now';
    if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
    if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
    if (diffSecs < 604800) return `${Math.floor(diffSecs / 86400)}d ago`;
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
export { DashboardService };
